import type {
  CartCreateResponse,
  CartResponseSuccess,
  ShippingInformationRequest,
  EstimateShippingMethodsRequest,
  EstimateShippingResponse,
  AddItemsToCartRequest,
  UpdateBundleItemsRequest,
  CartUpdatedResponseSuccess,
} from '@/types/cart'
import { createApiUtils } from '@/composables/api/repositories/utils'
import { useGuestStore } from '~/store/guest'
import { computed } from '#imports'

const baseUrl = '/cart/api/v2/cart'

type GuestCartResponses = {
  get: CartResponseSuccess
  create: CartCreateResponse
  init: CartResponseSuccess
  estimateShipping: EstimateShippingResponse
  addItems: CartResponseSuccess
  addShipping: CartUpdatedResponseSuccess
  updateBundle: CartResponseSuccess
  removeBundle: CartResponseSuccess
  updateOcrRequestId: CartResponseSuccess
  addPromotion: CartResponseSuccess
  removePromotion: CartResponseSuccess
}

export const useGuestCartRepository = () => {
  const guestStore = useGuestStore()
  const oboToken = computed(() => guestStore.guest?.token || '')
  const { execute, getError } = createApiUtils<GuestCartResponses>()

  const get = (id: string) =>
    ({
      url: `${baseUrl}/${id}`,
      options: {
        method: 'GET',
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const create = () =>
    ({
      url: baseUrl,
      options: {
        method: 'POST',
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const init = () =>
    ({
      url: `/cart/api/v3/cart`,
      options: {
        method: 'POST',
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const addItems = (id: string, items: AddItemsToCartRequest) =>
    ({
      url: `${baseUrl}/${id}/items`,
      options: {
        method: 'POST',
        body: items,
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const addShipping = (id: string, params: ShippingInformationRequest) =>
    ({
      url: `${baseUrl}/${id}/shipping-information`,
      options: {
        method: 'POST',
        body: {
          addressInformation: params,
        },
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const remove = (id: string) =>
    ({
      url: `${baseUrl}/${id}`,
      options: {
        method: 'DELETE',
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const estimateShipping = (
    id: string,
    params: EstimateShippingMethodsRequest,
  ) =>
    ({
      url: `${baseUrl}/${id}/estimate-shipping-methods`,
      options: {
        method: 'POST',
        body: params,
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const updateBundle = (
    id: string,
    bundleId: string,
    params: UpdateBundleItemsRequest,
  ) =>
    ({
      url: `${baseUrl}/${id}/bundles/${bundleId}`,
      options: {
        method: 'PATCH',
        body: params,
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const removeBundle = (id: string, bundleId: string) =>
    ({
      url: `${baseUrl}/${id}/bundles/${bundleId}`,
      options: {
        method: 'DELETE',
        body: {},
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const updateOcrRequestId = (
    id: string,
    bundleId: string,
    ocrRequestId: string | null,
  ) =>
    ({
      url: `${baseUrl}/${id}/bundles/${bundleId}/ocr-prescription-id`,
      options: {
        method: 'PATCH',
        body: {
          ocrRequestId: ocrRequestId ?? null,
        },
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const addPromotion = (id: string, promotionCode: string) =>
    ({
      url: `${baseUrl}/${id}/promotion`,
      options: {
        method: 'POST',
        body: {
          promotionCode,
        },
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  const removePromotion = (id: string) =>
    ({
      url: `${baseUrl}/${id}/promotion`,
      options: {
        method: 'DELETE',
        body: {},
        headers: {
          'x-zenni-store-id': guestStore.currentGift?.storeId ?? '',
          'x-authorization-obo': `Bearer ${oboToken.value}`,
        },
      },
    }) as const

  return {
    get,
    create,
    init,
    addItems,
    addShipping,
    remove,
    estimateShipping,
    updateBundle,
    removeBundle,
    updateOcrRequestId,
    addPromotion,
    removePromotion,
    execute,
    getError,
  }
}
